import debounce from "lodash-es/debounce";
import { useEffect, useMemo, useState } from "react";
import { FormProvider } from "react-hook-form";

import FlashContext from "components/FlashMessages/FlashContext";
import SkeletonLoader from "components/SkeletonLoader";
import { showFullDescription } from "ds/components/FullDescriptionDrawer";
import LoadingIndicator from "ds/components/LoadingIndicator";
import useTypedContext from "hooks/useTypedContext";
import { BlueprintParseResult, TemplateVersion } from "types/generated";

import BlueprintTemplateForm from "../components/DeploymentForm";
import useDeploymentForm from "../components/DeploymentForm/useDeploymentForm";
import Panel from "./Panel";

type FormPreviewProps = {
  body: string;
  version: TemplateVersion;
  validate: (template: string) => Promise<void | BlueprintParseResult>;
};

const DEBOUNCE_PREVIEW = 500;

const FormPreview = ({ body, version, validate }: FormPreviewProps) => {
  const [loading, setLoading] = useState(true);
  const [isExpanded, setExpanded] = useState(true);
  const { onError } = useTypedContext(FlashContext);
  const { builderForm, hasErrors, hasPreview, errors, setErrors, inputs, setInputs } =
    useDeploymentForm({
      version,
    });

  const handleShowFullDescription = () => {
    showFullDescription({ description: version.instructions });
  };

  const debouncedValidate = useMemo(
    () =>
      debounce(
        (template: string) =>
          validate(template)
            .then((result) => {
              setErrors(result?.errors || []);
              setInputs(result?.inputs || []);
            })
            .catch(onError)
            .finally(() => setLoading(false)),
        DEBOUNCE_PREVIEW
      ),
    [onError, setErrors, setInputs, validate]
  );

  useEffect(() => {
    if (isExpanded) {
      setLoading(true);
      debouncedValidate(body);
    }
  }, [body, debouncedValidate, isExpanded]);

  useEffect(() => {
    return () => {
      debouncedValidate.cancel();
    };
  }, [debouncedValidate]);

  return (
    <Panel
      title="Preview"
      actions={<LoadingIndicator loading={loading} size="default" />}
      side="right"
      onExpanded={setExpanded}
    >
      <FormProvider {...builderForm}>
        {loading && !inputs.length ? (
          <SkeletonLoader />
        ) : (
          <BlueprintTemplateForm
            item={version}
            isPublished={false}
            isPreviewMode
            onOpenFullDescription={handleShowFullDescription}
            hasErrors={hasErrors}
            hasPreview={hasPreview}
            errors={errors}
            inputs={inputs}
          />
        )}
      </FormProvider>
    </Panel>
  );
};

export default FormPreview;
